'use client';

import AuctionSidebar from './components/AuctionSidebar';
import AuctionCurrentBid from './components/AuctionCurrentBid';
import Breadcrumbs from '@components/Breadcrumbs';
import { FaFileAlt } from 'react-icons/fa';
import { useTranslations } from 'next-intl';
import useLocaleSuffix from '@/src/utils/useLocaleSuffix';
import ImageSlider from './components/ImageSlider';
import LoadingSkeletonItem from '@components/loadingSkeletons/loadingSkeletonItem';
import YouTubeVideo from './components/YouTubeVideo';
import ItemLocation from './components/Item/Location';
import ItemTitle from './components/Item/Title';
import ItemBody from './components/Item/Body';
import ItemSpecifications from './components/Item/Specifications';
import ItemMap from './components/ItemMap';
import ContactSeller from './components/ContactSeller';
import ContactSellerFixedPrice from './components/ContactSellerFixedPrice';
import { useEffect } from 'react';
import { setTitleTag } from '@/src/utils/titleTag';
import Favourite from './components/Favourite';
import SalesCost from './components/SalesCost';
import ShippingInfo from './components/ShippingInfo';
import getOption from '@/src/utils/getOption';
import ErrorBoundary from '@components/ErrorBoundary';
import ShippingCalculator from './components/ShippingCalculator';
import FinancialCalculator from './components/FinancingCalculator';
import getAdPrice from '@/src/utils/getAdPrice';
import AdminEditButton from '@components/AdminEditButton';
import Disclaimer from '@components/Disclaimer';
import RoadAdminInfo from './components/RoadAdminInfo';
import StickyBox from 'react-sticky-box';
import './styles.css';
import useShowPrice from '@/src/utils/useShowPrice';
import { useGetCategoryById } from '@/src/utils/categories/useGetCategoryById';

export default function ClientPage({ ad, id }: { ad: any; id: number }) {
  const t = useTranslations('page.item');
  const getLocaleSuffix = useLocaleSuffix();
  const showPrice = useShowPrice(ad);
  const getCategoryById = useGetCategoryById();
  const categoriesTree = [];
  let parentCategory = ad.category_id;
  while (parentCategory) {
    categoriesTree.push(parentCategory);
    parentCategory = getCategoryById(parentCategory)?.parent_id;
    if (!parentCategory) {
      break;
    }
  }

  let breadcrumbsList = [] as any;
  let path = '';
  if (categoriesTree.length) {
    categoriesTree.reverse().forEach((category) => {
      category = getCategoryById(category);
      path += `/${category?.category_slug?.[getLocaleSuffix()]}`;
      breadcrumbsList.push({
        label: category?.category_name?.[getLocaleSuffix()],
        path,
      });
    });
  }

  const youtubeId = ad?.youtube ?? false;
  const images = ad && ad.ad_images ? ad.ad_images : false;
  const imagesUrls = images ? ad.ad_images : false;

  const title = ad?.[getLocaleSuffix('title')];
  useEffect(() => {
    if (title) {
      setTitleTag(title);
    }
  }, [title]);

  if (!ad) {
    return (
      <div className='mx-auto w-full'>
        <LoadingSkeletonItem />
      </div>
    );
  }

  return (
    <div className='mx-auto w-full h-md-down:max-w-[70rem]'>
      {!!breadcrumbsList.length && (
        <section className='my-4'>
          <Breadcrumbs items={breadcrumbsList} />
        </section>
      )}

      <section className='relative'>
        {/* START AD CONTAINER */}
        <section className='flex flex-col gap-6 pr-6 lg:float-left lg:w-2/3'>
          <div className='-mb-6 flex justify-between rounded-t bg-green px-2 py-1  text-white/70'>
            <span className='font-semibold'># {ad.id}</span>
            <span className='uppercase'>
              {t('seller')}:{' '}
              <span className='font-semibold'>
                {ad.customer?.is_private ? t('private') : t('company')}
              </span>
            </span>
          </div>
          {!!imagesUrls.length && (
            <ImageSlider images={imagesUrls} id={ad.id} />
          )}
          <div className='flex flex-wrap justify-between gap-2 md:flex-nowrap lg:flex-wrap xl:flex-nowrap'>
            <ItemTitle title={ad[getLocaleSuffix('title')]} />
            {getOption('SHOW_USER_FUNCTIONS') && <Favourite adId={ad.id} />}
          </div>
        </section>
        {/* START SIDEBAR */}
        <StickyBox
          offsetTop={190}
          className='sticky-cont lg:sticky lg:top-[12em] lg:float-right lg:w-1/3'
        >
          <section className='sidebar lg:stiky lg:top[12rem] relative right-0 z-30 mx-auto flex w-full max-w-md flex-col gap-10 lg:col-span-4'>
            <div className='rounded-lg bg-brand text-dark'>
              {ad && ad.ad_auction ? (
                <ErrorBoundary layout='sm'>
                  <AuctionSidebar ad={ad} />
                </ErrorBoundary>
              ) : (
                <>
                  {getOption('HAS_FIXED_PRICE') && (
                    <>
                      <AuctionCurrentBid
                        bid={typeof ad.price === 'string' ? parseInt(ad.price.replace(/\s/g, '')) : ad.price}
                        isAuction={false}
                        showPrice={showPrice}
                        adStatus={ad.status}
                        sold={ad.sold}
                      />
                      {showPrice && (
                        <SalesCost excludingVat={ad.price_ex_vat} />
                      )}
                    </>
                  )}
                </>
              )}
              {!!ad.ad_auction ? (
                <ContactSeller />
              ) : (
                <>
                  <ContactSellerFixedPrice />
                  <div className='flex flex-col gap-4 px-6 pb-6'>
                    <FinancialCalculator
                      price={showPrice && getAdPrice(ad)}
                      refId={`#${ad.id} ${String(title)}`}
                    />
                    {getOption('SHIPPING_CALCULATOR') && ad.shippingclass ? (
                      <ShippingCalculator
                        adId={ad.id}
                        shippingType={ad.shippingclass}
                      />
                    ) : (
                      <ShippingInfo />
                    )}
                  </div>
                </>
              )}
            </div>
            <div className='-top-14 w-full lg:absolute'>
              <AdminEditButton id={ad.id} />
            </div>
          </section>
        </StickyBox>
        {/* END SIDEBAR */}
        <section className='flex flex-col gap-6 pr-6 lg:col-span-8 lg:float-left lg:w-2/3'>
          <div className='flex flex-wrap justify-between gap-4 font-bold'>
            <div>
              <ItemLocation location={ad.municipality} />
            </div>
            Ref. nr: {ad.id}
          </div>
          <ItemSpecifications ad={ad} />
          <ItemBody body={ad[getLocaleSuffix('descriptionHTML')]} />
          {youtubeId && <YouTubeVideo videoId={youtubeId} />}
          <ItemMap
            county={ad.county_id}
            city={ad.municipality}
            zipcode={ad.zipcode}
          />
          {!!ad.ad_auction && (
            <div className='flex flex-col gap-4 px-6 pb-6'>
              <FinancialCalculator
                price={showPrice && getAdPrice(ad)}
                refId={`#${ad.id} ${String(title)}`}
              />
              {getOption('SHIPPING_CALCULATOR') && ad.shippingclass ? (
                <ShippingCalculator
                  adId={ad.id}
                  shippingType={ad.shippingclass}
                />
              ) : (
                <ShippingInfo />
              )}
            </div>
          )}
          {!!ad.ad_files.length && (
            <>
              <h3 className='font-semibold'>{t('files')}</h3>
              {ad.ad_files.map((file: any) => {
                if (!file.published) {
                  return null;
                }
                return (
                  <a
                    href={file.url}
                    className='flex items-center gap-2 hover:underline'
                    target='_blank'
                    key={file.id}
                  >
                    <FaFileAlt /> {file.filename}
                  </a>
                );
              })}
            </>
          )}
          <RoadAdminInfo
            vegvesen={ad.vegvesen}
            vegvesenjson={ad.vegvesenjson}
          />
          <RoadAdminInfo
            vegvesen={ad.vegvesen2}
            vegvesenjson={ad.vegvesenjson2}
          />
          <Disclaimer />
        </section>
        {/* END AD CONTAINER */}

        <div className='clear-both'></div>
      </section>
    </div>
  );
}
